import React, { useState, useEffect, useMemo, useCallback } from "react"
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Drawer,
  InputAdornment,
} from "@mui/material"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import EditNoteIcon from "@mui/icons-material/EditNote"
import PersonIcon from "@mui/icons-material/Person"
import DescriptionIcon from "@mui/icons-material/Description"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import DownloadIcon from "@mui/icons-material/Download"
import ViewInArIcon from "@mui/icons-material/ViewInAr"
import RefreshIcon from "@mui/icons-material/Refresh"
import FolderZipIcon from "@mui/icons-material/FolderZip"
import EditIcon from "@mui/icons-material/Edit"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import AddIcon from "@mui/icons-material/Add"
import SendIcon from "@mui/icons-material/Send"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import FileUploadIcon from "@mui/icons-material/FileUpload"
import TableChartIcon from "@mui/icons-material/TableChart"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  getAllQuotes,
  getQuotesByStatus,
  updateQuote,
  getProductsBySKUs,
  searchProductsByText,
  postOrder,
} from "helpers/api_helper"
import * as XLSX from "xlsx"
import { getEntityFiles, downloadFile, uploadFile } from "helpers/fileStorage"
import {
  generateCustomerPDF,
  generateSupplierPDF,
} from "helpers/storePdfExport"
import { useSelector } from "react-redux"
import { selectCompanyDetails } from "store/company/reducer"
import JSZip from "jszip"
import { useDynamicCatalog } from "pages/Store/cpq/useDynamicCatalog"
import { splitSwatches } from "constants/catalogData"

const STATUS_CONFIG = {
  pending_approval: { bg: "#fef3c7", color: "#92400e", label: "Pending Approval" },
  approved: { bg: "#dcfce7", color: "#166534", label: "Approved" },
  changes_requested: { bg: "#fee2e2", color: "#991b1b", label: "Changes Requested" },
  revised: { bg: "#dbeafe", color: "#1d4ed8", label: "Revised" },
  final_accepted: { bg: "#d1fae5", color: "#065f46", label: "Final Accepted" },
  draft: { bg: "#f1f5f9", color: "#475569", label: "Draft" },
}

const STATUS_FILTERS = [
  { value: "pending_approval", label: "Pending" },
  { value: "revised", label: "Revised" },
  { value: "final_accepted", label: "Accepted" },
  { value: "approved", label: "Approved" },
  { value: "changes_requested", label: "Changes" },
  { value: "all", label: "All" },
]

// Parse cart from quote
const parseCart = (quote) => {
  if (!quote?.cart) return []
  try {
    return typeof quote.cart === "string" ? JSON.parse(quote.cart) : quote.cart
  } catch { return [] }
}

// Parse config from quote
const parseConfig = (quote) => {
  if (!quote?.config) return {}
  try {
    return typeof quote.config === "string" ? JSON.parse(quote.config) : quote.config
  } catch { return {} }
}

// Group cart items by cabinet_position
const groupCartItems = (cart) => {
  const groups = { Wall: [], Base: [], Tall: [], Accessories: [], Other: [] }
  cart.forEach(item => {
    const posMap = { BASE: "Base", WALL: "Wall", TALL: "Tall" }
    const pos = item.cabinet_position ? posMap[item.cabinet_position.toUpperCase()] : null
    if (pos && groups[pos]) {
      groups[pos].push(item)
    } else {
      let cats = []
      try { cats = JSON.parse(item.category || "[]") } catch { cats = [] }
      if (cats.includes("Accessories")) {
        groups.Accessories.push(item)
      } else {
        groups.Other.push(item)
      }
    }
  })
  return groups
}

// ─── Quote Card (Left Panel) ──────────────────────────────────
const QuoteCard = ({ quote, isSelected, onClick, t }) => {
  const status = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft
  const updatedAt = quote.updated_at
    ? new Date(quote.updated_at).toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : ""
  const itemCount = quote.item_count || 0
  const total = parseFloat(quote.total_price) || 0

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        cursor: "pointer",
        border: isSelected ? "2px solid #3b82f6" : "1px solid #e2e8f0",
        bgcolor: isSelected ? "#eff6ff" : "#fff",
        transition: "all 0.15s",
        "&:hover": { borderColor: "#3b82f6", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {quote.title || "Untitled"}
            </Typography>
            <Chip
              label={t(status.label)}
              size="small"
              sx={{ height: 18, fontSize: "0.52rem", fontWeight: 700, bgcolor: status.bg, color: status.color }}
            />
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {quote.customer_name && (
              <Stack direction="row" alignItems="center" spacing={0.3}>
                <PersonIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                <Typography sx={{ fontSize: "0.68rem", color: "#64748b" }}>{quote.customer_name}</Typography>
              </Stack>
            )}
            <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8" }}>{updatedAt}</Typography>
          </Stack>
        </Box>
        <Stack alignItems="flex-end">
          {total > 0 && (
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#1e293b" }}>
              {"\u20AC"}{total.toFixed(2)}
            </Typography>
          )}
          <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>
            {itemCount} {t("items")}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  )
}

// ─── Pytha Files Section ──────────────────────────────────────
const PythaFilesSection = ({ cart, t, filesByProduct, loadingFiles }) => {
  const productFiles = Object.entries(filesByProduct)
  const totalFileCount = productFiles.reduce((sum, [, files]) => sum + files.length, 0)

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#faf5ff", border: "1px solid #e9d5ff" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ViewInArIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#6b21a8" }}>
          {t("Pytha 3D Files")}
        </Typography>
        {!loadingFiles && (
          <Chip
            label={`${totalFileCount} ${t("files")}`}
            size="small"
            sx={{ height: 18, fontSize: "0.58rem", fontWeight: 700, bgcolor: totalFileCount > 0 ? "#d8b4fe" : "#e2e8f0", color: totalFileCount > 0 ? "#6b21a8" : "#94a3b8" }}
          />
        )}
      </Stack>
      {loadingFiles ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
          <CircularProgress size={14} sx={{ color: "#8b5cf6" }} />
          <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>{t("Loading Pytha files...")}</Typography>
        </Stack>
      ) : productFiles.length === 0 ? (
        <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", py: 0.5 }}>
          {t("No Pytha files found for products in this quote")}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {productFiles.map(([pid, files]) => {
            const product = cart.find(i => String(i.id) === String(pid))
            return files.map(file => (
              <Stack key={file.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: "#f5f3ff", "&:hover": { bgcolor: "#ede9fe" } }}>
                <ViewInArIcon sx={{ fontSize: 14, color: "#a78bfa" }} />
                <Typography sx={{ fontSize: "0.7rem", color: "#475569", flex: 1 }}>
                  <strong>{product?.name || `Product #${pid}`}</strong> — {file.original_filename || "pytha3d"}
                </Typography>
                <Tooltip title={t("Download file")}>
                  <IconButton
                    size="small"
                    onClick={() => downloadFile(file.id, file.original_filename || "pytha3d-file")}
                    sx={{ color: "#7c3aed", bgcolor: "#ede9fe", "&:hover": { bgcolor: "#ddd6fe" } }}
                  >
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))
          })}
        </Stack>
      )}
    </Paper>
  )
}

// ─── Import Preview/Error Dialog ─────────────────────────────────
const ImportPreviewDialog = ({ open, onClose, onApply, matchedProducts, missingSkus, t }) => {
  const allValid = missingSkus.length === 0
  const totalSkus = matchedProducts.length + missingSkus.length

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t("Import Preview")}
        <Typography sx={{ fontSize: "0.82rem", color: "#64748b", mt: 0.5 }}>
          {matchedProducts.length} {t("of")} {totalSkus} {t("SKUs verified")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {missingSkus.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{missingSkus.length} {t("SKU(s) not found in database")}:</strong>
            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {missingSkus.map(sku => (
                <Chip key={sku} label={sku} size="small" sx={{ fontFamily: "monospace", fontWeight: 700, bgcolor: "#fee2e2", color: "#991b1b" }} />
              ))}
            </Box>
          </Alert>
        )}
        {matchedProducts.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem" }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem" }}>{t("Product")}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>{t("Qty")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>B2B {t("Price")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matchedProducts.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}>{item.sku}</TableCell>
                    <TableCell sx={{ fontSize: "0.72rem" }}>{item.name}</TableCell>
                    <TableCell align="center" sx={{ fontSize: "0.72rem", fontWeight: 700 }}>{item.quantity}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.72rem" }}>{"\u20AC"}{(parseFloat(item.b2bsalesprice) || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>{t("Cancel")}</Button>
        <Button
          variant="contained"
          onClick={onApply}
          disabled={!allValid}
          sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
        >
          {t("Apply")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Quote Detail Panel (Right) ─────────────────────────────────
const QuoteDetail = ({ quote, onApprove, onRequestChanges, onRefresh, onSaveEdits, navigate, t }) => {
  const companyDetails = useSelector(selectCompanyDetails)
  const cart = useMemo(() => parseCart(quote), [quote])
  const config = useMemo(() => parseConfig(quote), [quote])

  // Edit mode state
  const [editMode, setEditMode] = useState(false)
  const [editedCart, setEditedCart] = useState([])
  const [specialistName, setSpecialistName] = useState("")
  const [editNotes, setEditNotes] = useState("")
  const [saving, setSaving] = useState(false)

  // Review file attachments (stored on quote level)
  const [reviewPdf, setReviewPdf] = useState(quote.review_pdf_url || null)
  const [reviewPytha, setReviewPytha] = useState(quote.review_pytha_url || null)
  const [reviewCsv, setReviewCsv] = useState(quote.review_csv_url || null)
  const [uploadingFile, setUploadingFile] = useState(null) // "pdf" | "pytha" | "csv" | null
  const [missingFilesDialogOpen, setMissingFilesDialogOpen] = useState(false)
  const reviewPdfRef = React.useRef(null)
  const reviewPythaRef = React.useRef(null)
  const reviewCsvRef = React.useRef(null)

  // Pytha files state (lifted to share between section and zip export)
  const [filesByProduct, setFilesByProduct] = useState({})
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [zipping, setZipping] = useState(false)

  // CSV Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importMatchedProducts, setImportMatchedProducts] = useState([])
  const [importMissingSkus, setImportMissingSkus] = useState([])
  const [importing, setImporting] = useState(false)
  const fileInputRef = React.useRef(null)

  // Product search drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerSection, setDrawerSection] = useState("Base")
  const [searchText, setSearchText] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [expandedProduct, setExpandedProduct] = useState(null) // product with parsed variants
  const [drawerModel, setDrawerModel] = useState("") // selected finish_code in drawer
  const [drawerSwatch, setDrawerSwatch] = useState(null) // selected swatch object in drawer
  const searchTimerRef = React.useRef(null)

  // Load swatch catalog for model→color selection in drawer
  const { swatchesByGroup } = useDynamicCatalog()

  // Create Order state
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false)
  const [orderCreated, setOrderCreated] = useState(null)
  const [creatingOrder, setCreatingOrder] = useState(false)

  // Load pytha files
  useEffect(() => {
    const loadFiles = async () => {
      setLoadingFiles(true)
      const productIds = [...new Set(cart.map(item => item.id).filter(Boolean))]
      const results = {}
      await Promise.all(
        productIds.map(async (pid) => {
          try {
            const files = await getEntityFiles("product-pytha3d", pid)
            if (files && files.length > 0) results[pid] = files
          } catch { /* ignore */ }
        })
      )
      setFilesByProduct(results)
      setLoadingFiles(false)
    }
    if (cart.length > 0) loadFiles()
  }, [cart])

  // Reset edit mode when quote changes
  useEffect(() => {
    setEditMode(false)
    setEditedCart([])
    setSpecialistName("")
    setEditNotes("")
    setReviewPdf(quote.review_pdf_url || null)
    setReviewPytha(quote.review_pytha_url || null)
    setReviewCsv(quote.review_csv_url || null)
  }, [quote.id])

  const displayCart = editMode ? editedCart : cart
  const groups = useMemo(() => groupCartItems(displayCart), [displayCart])

  const profitMargin = config.profitMargin || 0
  const calculateFinalPrice = (b2bPrice) => {
    const price = parseFloat(b2bPrice) || 0
    if (profitMargin === 0) return price
    return price * (1 + profitMargin / 100)
  }

  const b2bSubtotal = displayCart.reduce((sum, i) => sum + (parseFloat(i.b2bsalesprice) || 0) * (i.quantity || 1), 0)
  const customerSubtotal = displayCart.reduce((sum, i) => sum + calculateFinalPrice(parseFloat(i.b2bsalesprice) || 0) * (i.quantity || 1), 0)

  // RAL custom-color surcharge: €180 flat per distinct RAL code across the whole quote
  const RAL_SURCHARGE_PER_CODE = 180
  const ralCodes = useMemo(() => {
    const set = new Set()
    for (const i of displayCart) {
      const code = (i.customRalCode || "").trim()
      if (code) set.add(code.toUpperCase())
    }
    return [...set]
  }, [displayCart])
  const ralSurchargeTotal = ralCodes.length * RAL_SURCHARGE_PER_CODE

  const b2bTotal = b2bSubtotal + ralSurchargeTotal
  const customerTotal = customerSubtotal + ralSurchargeTotal
  const marginAmount = customerTotal - b2bTotal

  const status = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft

  // Config chips
  const configChips = useMemo(() => {
    const chips = []
    if (config.kasoma) chips.push(`Kasoma: ${config.kasoma}`)
    if (config.pomolo) chips.push(`Pomolo: ${config.pomolo}`)
    if (config.model) chips.push(`Model: ${config.model}`)
    if (config.swatch?.label) chips.push(`Color: ${config.swatch.label}`)
    if (profitMargin > 0) chips.push(`Margin: ${profitMargin}%`)
    return chips
  }, [config, profitMargin])

  // ─── ZIP Export ───────────────────────────────────────────────
  const handleZipExport = async () => {
    setZipping(true)
    try {
      const zip = new JSZip()
      const allFiles = Object.entries(filesByProduct)

      for (const [pid, files] of allFiles) {
        const product = cart.find(i => String(i.id) === String(pid))
        const folderName = product ? `${product.sku || "PROD"}_${product.name || pid}`.replace(/[^a-zA-Z0-9_\-.\s]/g, "").trim() : `Product_${pid}`

        for (const file of files) {
          try {
            const blob = await downloadFile(file.id) // no filename = returns blob without browser download
            zip.file(`${folderName}/${file.original_filename || `file_${file.id}`}`, blob)
          } catch { /* skip failed downloads */ }
        }
      }

      const content = await zip.generateAsync({ type: "blob" })
      const url = URL.createObjectURL(content)
      const a = document.createElement("a")
      a.href = url
      a.download = `${quote.title || "Quote"}_PythaFiles.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error("[QuoteReview] ZIP export error:", err)
    } finally {
      setZipping(false)
    }
  }

  // ─── Edit Mode ────────────────────────────────────────────────
  const handleEnterEditMode = () => {
    setEditedCart(JSON.parse(JSON.stringify(cart))) // deep clone
    setSpecialistName("")
    setEditNotes("")
    setEditMode(true)
  }

  const handleCancelEdit = () => {
    setEditMode(false)
    setEditedCart([])
  }

  const handleEditField = (cartItemKey, field, value) => {
    setEditedCart(prev => prev.map(item =>
      item.cartItemKey === cartItemKey ? { ...item, [field]: value } : item
    ))
  }

  const handleRemoveItem = (cartItemKey) => {
    setEditedCart(prev => prev.filter(item => item.cartItemKey !== cartItemKey))
  }

  // Map section key to cabinet_position value for new items
  const SECTION_TO_POSITION = { Wall: "WALL", Base: "BASE", Tall: "TALL", Accessories: "ACCESSORIES", Other: "OTHER" }

  const handleOpenSearchDrawer = (sectionKey) => {
    setDrawerSection(sectionKey)
    setSearchText("")
    setSearchResults([])
    setExpandedProduct(null)
    setDrawerModel("")
    setDrawerSwatch(null)
    setDrawerOpen(true)
  }

  const handleSearchProducts = useCallback((text) => {
    setSearchText(text)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (!text || text.trim().length < 2) {
      setSearchResults([])
      return
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true)
      try {
        const results = await searchProductsByText(text.trim(), 25)
        setSearchResults(results)
      } catch (err) {
        console.error("[QuoteReview] Product search error:", err)
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 400)
  }, [])

  const handleProductClick = (product) => {
    // Parse variants
    let variants = []
    try { variants = JSON.parse(product.variants || "[]") } catch { /* ignore */ }
    // Only show priced variants
    variants = Array.isArray(variants) ? variants.filter(v => v.b2bsalesprice != null) : []

    if (variants.length > 0) {
      // Reset model and swatch selections
      setDrawerModel("")
      setDrawerSwatch(null)
      setExpandedProduct({ ...product, _variants: variants })
    } else {
      // No variants — add directly
      handleAddFromSearch(product)
    }
  }

  const handleAddFromSearch = (product, variantOverride, swatchOverride, modelCode) => {
    const variant = variantOverride || {}
    const newKey = `specialist_${Date.now()}_${product.id}_${variant.sku || ""}`
    const position = SECTION_TO_POSITION[drawerSection] || "BASE"
    setEditedCart(prev => [...prev, {
      cartItemKey: newKey,
      id: product.id,
      sku: variant.sku || product.sku || "",
      name: product.name || "",
      productWidth: variant.width || product.width || 0,
      productHeight: variant.height || product.height || 0,
      productDepth: variant.depth || product.depth || 0,
      quantity: 1,
      b2bsalesprice: variant.b2bsalesprice || product.b2bsalesprice || 0,
      cabinet_position: position,
      category: product.category || "[]",
      swatch: swatchOverride || null,
      modelCode: modelCode || "",
    }])
    // Don't close drawer — user may want to add more products
  }

  // ─── Review file upload handler ──────────────────────────────
  const handleReviewFileUpload = async (file, type) => {
    if (!file) return
    setUploadingFile(type)
    try {
      const result = await uploadFile(file, `quote-review/${quote.id}`, null, {
        entity_type: "quote-review",
        entity_id: quote.id,
      })
      if (result?.url) {
        if (type === "pdf") setReviewPdf(result.url)
        else if (type === "pytha") setReviewPytha(result.url)
        else if (type === "csv") setReviewCsv(result.url)
      }
    } catch (err) {
      console.error(`[QuoteReview] Upload ${type} failed:`, err)
    } finally {
      setUploadingFile(null)
    }
  }

  // ─── Save & Send Back (with file validation) ──────────────────
  const doSaveAndSendBack = async () => {
    setSaving(true)
    try {
      const itemCount = editedCart.reduce((s, i) => s + (parseInt(i.quantity) || 1), 0)
      const totalPrice = editedCart.reduce((s, i) => s + calculateFinalPrice(parseFloat(i.b2bsalesprice) || 0) * (parseInt(i.quantity) || 1), 0)

      await onSaveEdits(quote.id, {
        cart: editedCart,
        item_count: itemCount,
        total_price: totalPrice.toFixed(2),
        status: "revised",
        reviewed_by: specialistName.trim(),
        reviewed_at: new Date().toISOString(),
        review_notes: editNotes || "",
        review_pdf_url: reviewPdf || null,
        review_pytha_url: reviewPytha || null,
        review_csv_url: reviewCsv || null,
        updated_at: new Date().toISOString(),
      })
      setEditMode(false)
    } catch (err) {
      console.error("[QuoteReview] Save edits error:", err)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAndSendBack = async () => {
    if (!specialistName.trim()) return
    const missing = []
    if (!reviewPdf) missing.push(t("Production PDF"))
    if (!reviewPytha) missing.push(t("Pytha File"))
    if (!reviewCsv) missing.push(t("Dimensions CSV"))
    if (missing.length > 0) {
      setMissingFilesDialogOpen(true)
      return
    }
    await doSaveAndSendBack()
  }

  // ─── PDF Export ───────────────────────────────────────────────
  const getPdfParams = () => {
    const formatCustomDimensions = (cd) => {
      if (!cd) return "Stock Size"
      const parts = []
      if (cd.length) parts.push(`L: ${cd.length}`)
      if (cd.height) parts.push(`H: ${cd.height}`)
      return parts.length > 0 ? parts.join(" x ") + "mm" : "Stock Size"
    }

    const PARENT_CATEGORIES = ["Cabinet", "Accessories", "Special", "Charges"]
    const cartGroups = {}
    displayCart.forEach(item => {
      const posMap = { BASE: "Base", WALL: "Wall", TALL: "Tall" }
      let cats = []
      try { cats = JSON.parse(item.category || "[]") } catch { cats = [] }
      const rawCat1 = cats[0] === "KITCHEN" ? "Cabinet" : cats[0]
      const cat1Key = rawCat1 && PARENT_CATEGORIES.includes(rawCat1) ? rawCat1 : "Other"
      const cat2Key = (item.cabinet_position && posMap[item.cabinet_position.toUpperCase()]) || cats[1] || "Other"
      if (!cartGroups[cat1Key]) cartGroups[cat1Key] = {}
      if (!cartGroups[cat1Key][cat2Key]) cartGroups[cat1Key][cat2Key] = []
      cartGroups[cat1Key][cat2Key].push(item)
    })

    const cat1Totals = {}
    const cat2Subtotals = {}
    for (const [c1, c2g] of Object.entries(cartGroups)) {
      cat1Totals[c1] = 0
      cat2Subtotals[c1] = {}
      for (const [c2, items] of Object.entries(c2g)) {
        const sub = items.reduce((s, i) => s + calculateFinalPrice(parseFloat(i.b2bsalesprice) || 0) * i.quantity, 0)
        cat2Subtotals[c1][c2] = sub
        cat1Totals[c1] += sub
      }
    }
    const orderedCat1Keys = PARENT_CATEGORIES.filter(k => cartGroups[k])
    if (cartGroups["Other"]) orderedCat1Keys.push("Other")

    const cartItemCount = displayCart.reduce((s, i) => s + (i.quantity || 1), 0)

    return {
      companyDetails,
      groupedCart: { groups: cartGroups, cat1Totals, cat2Subtotals, orderedCat1Keys },
      cart: displayCart,
      profitMargin,
      cartTotal: customerTotal,
      cartItemCount,
      calculateFinalPrice,
      formatCustomDimensions,
      currentUser: {},
      t,
      customerInfo: {
        name: quote.customer_name || "",
        phone: quote.customer_phone || "",
        email: quote.customer_email || "",
        address: quote.customer_address || "",
      },
      quoteTitle: quote.title || "",
      settingsChips: configChips.map(c => ({ label: c })),
    }
  }

  const handleExportCustomer = () => generateCustomerPDF(getPdfParams())
  const handleExportSupplier = () => generateSupplierPDF(getPdfParams())

  // ─── CSV Export ─────────────────────────────────────────────────
  const handleCsvExport = () => {
    const rows = displayCart.map(item => ({
      SKU: item.sku || "",
      "Product Name": item.name || "",
      "Door Option": item.doorOption || "",
      "Κρεμαση CM": item.hingeCm || "",
      Unit: item.unit || "Item",
      "Unit Qty": item.unitQuantity || "",
      "Unit Rate": item.unitRate || "",
      W: parseInt(item.customDimensions?.length || item.productWidth) || 0,
      H: parseInt(item.customDimensions?.height || item.productHeight) || 0,
      D: parseInt(item.customDimensions?.depth || item.productDepth) || 0,
      Qty: parseInt(item.quantity) || 1,
      "B2B Price": parseFloat(item.b2bsalesprice) || 0,
      "Customer Price": calculateFinalPrice(parseFloat(item.b2bsalesprice) || 0),
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Quote")
    XLSX.writeFile(wb, `${quote.title || "Quote"}_${quote.id}.xlsx`)
  }

  // ─── Create ERP Order ──────────────────────────────────────────
  const handleCreateOrder = async () => {
    setCreatingOrder(true)
    try {
      const products = displayCart.map(item => {
        const b2b = parseFloat(item.b2bsalesprice) || 0
        const custPrice = calculateFinalPrice(b2b)
        const qty = parseInt(item.quantity) || 1
        return {
          id: item.id,
          name: item.name,
          sku: item.sku,
          salesprice: custPrice,
          b2bsalesprice: b2b,
          quantity: qty,
          vat: 24,
          totalPrdAmmount: (qty * custPrice).toFixed(2),
          vatProducts: (qty * custPrice * 0.24).toFixed(2),
          productWidth: item.customDimensions?.length || item.productWidth,
          productHeight: item.customDimensions?.height || item.productHeight,
          productDepth: item.customDimensions?.depth || item.productDepth,
          cabinet_position: item.cabinet_position,
          category: item.category,
          unit: item.unit || "Item",
          unitQuantity: item.unitQuantity || null,
          unitRate: item.unitRate || null,
        }
      })

      const totalProductsAmmount = products.reduce((s, p) => s + parseFloat(p.totalPrdAmmount), 0)
      const totalVat = products.reduce((s, p) => s + parseFloat(p.vatProducts), 0)
      const totalItems = products.reduce((s, p) => s + p.quantity, 0)
      const totalOrder = (totalProductsAmmount + totalVat).toFixed(2)

      const totals = {
        totalProductsAmmount,
        totalVat,
        totalItems,
        totalOrder,
      }

      const orderPayload = {
        resource: [{
          name: quote.customer_name || "",
          label: quote.customer_name || "",
          delivery_address: JSON.stringify({ address: quote.customer_address || "" }),
          products: JSON.stringify(products),
          totals: JSON.stringify(totals),
          description: `Quote: ${quote.title || ""}`,
          status: "OFFER",
          customer_id: null,
          loans: "[]",
          services: "[]",
          is_intra_community: false,
          expected_delivery_date: new Date().toISOString(),
        }],
      }

      const result = await postOrder(orderPayload)
      const newOrderId = result?.resource?.[0]?.id || result?.id || null
      setOrderCreated({ id: newOrderId })
      setCreateOrderDialogOpen(false)
    } catch (err) {
      console.error("[QuoteReview] Create order error:", err)
      alert("Failed to create order: " + (err.message || "Unknown error"))
    } finally {
      setCreatingOrder(false)
    }
  }

  // ─── CSV Import ─────────────────────────────────────────────────
  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Reset input so same file can be re-selected
    e.target.value = ""

    setImporting(true)
    try {
      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: "array" })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" })

      // Flexible column name matching
      const findCol = (row, candidates) => {
        for (const c of candidates) {
          const key = Object.keys(row).find(k => k.toLowerCase().trim() === c.toLowerCase())
          if (key !== undefined) return key
        }
        return null
      }

      if (rows.length === 0) {
        setImporting(false)
        return
      }

      const skuCol = findCol(rows[0], ["sku", "SKU", "Sku", "sku code", "SKU Code"])
      const qtyCol = findCol(rows[0], ["qty", "Qty", "QTY", "quantity", "Quantity", "QUANTITY"])

      if (!skuCol) {
        alert("Could not find SKU column in the file. Expected: SKU, sku, or Sku")
        setImporting(false)
        return
      }

      // Extract unique SKUs and quantities
      const skuQtyMap = {}
      rows.forEach(row => {
        const sku = String(row[skuCol]).trim()
        if (!sku) return
        const qty = qtyCol ? (parseInt(row[qtyCol]) || 1) : 1
        skuQtyMap[sku] = (skuQtyMap[sku] || 0) + qty
      })

      const uniqueSkus = Object.keys(skuQtyMap)
      if (uniqueSkus.length === 0) {
        setImporting(false)
        return
      }

      // Validate against DB
      const dbProducts = await getProductsBySKUs(uniqueSkus)
      const dbSkuMap = {}
      dbProducts.forEach(p => { dbSkuMap[p.sku] = p })

      const matched = []
      const missing = []

      uniqueSkus.forEach(sku => {
        const product = dbSkuMap[sku]
        if (product) {
          matched.push({
            ...product,
            quantity: skuQtyMap[sku],
          })
        } else {
          missing.push(sku)
        }
      })

      setImportMatchedProducts(matched)
      setImportMissingSkus(missing)
      setImportDialogOpen(true)
    } catch (err) {
      console.error("[QuoteReview] CSV import error:", err)
      alert("Error reading file: " + err.message)
    } finally {
      setImporting(false)
    }
  }

  const handleApplyImport = () => {
    // Build cart items from matched DB products, preserving swatch from existing cart
    const newCart = importMatchedProducts.map((product, idx) => {
      const existing = cart.find(c => c.sku === product.sku)
      return {
        cartItemKey: existing?.cartItemKey || `import_${Date.now()}_${idx}`,
        id: product.id,
        sku: product.sku || "",
        name: product.name || "",
        productWidth: existing?.productWidth || product.productWidth || product.width || 0,
        productHeight: existing?.productHeight || product.productHeight || product.height || 0,
        productDepth: existing?.productDepth || product.productDepth || product.depth || 0,
        quantity: product.quantity || 1,
        b2bsalesprice: product.b2bsalesprice || 0,
        cabinet_position: product.cabinet_position || "BASE",
        category: product.category || "[]",
        swatch: existing?.swatch || null,
      }
    })

    setEditedCart(newCart)
    if (!editMode) {
      setSpecialistName("")
      setEditNotes("")
      setEditMode(true)
    }
    setImportDialogOpen(false)
  }

  const sectionOrder = ["Wall", "Base", "Tall", "Accessories", "Other"]
  const SECTION_LABELS = { Wall: "Wall Cabinets", Base: "Base Cabinets", Tall: "Tall Cabinets", Accessories: "Accessories", Other: "Other" }
  const SECTION_COLORS = { Wall: "#2563eb", Base: "#16a34a", Tall: "#7c3aed", Accessories: "#d97706", Other: "#64748b" }

  const hasPythaFiles = Object.keys(filesByProduct).length > 0

  return (
    <Box sx={{ p: 3, overflow: "auto", height: "100%" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
              {quote.title || "Untitled Quote"}
            </Typography>
            <Chip
              label={t(status.label)}
              sx={{ fontWeight: 700, fontSize: "0.72rem", bgcolor: status.bg, color: status.color }}
            />
            {editMode && (
              <Chip label={t("Editing")} size="small" sx={{ fontWeight: 700, fontSize: "0.65rem", bgcolor: "#fef3c7", color: "#92400e" }} />
            )}
          </Stack>
          <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
            ID: {quote.id} | {quote.updated_at ? new Date(quote.updated_at).toLocaleString("el-GR") : ""}
          </Typography>
          {quote.reviewed_by && (
            <Typography sx={{ fontSize: "0.75rem", color: "#7c3aed", mt: 0.25 }}>
              {t("Reviewed by")}: {quote.reviewed_by} | {quote.reviewed_at ? new Date(quote.reviewed_at).toLocaleString("el-GR") : ""}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          {!editMode && (
            <Tooltip title={t("Refresh")}>
              <IconButton onClick={onRefresh} sx={{ color: "#64748b" }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Review notes from specialist (if any) */}
      {quote.review_notes && !editMode && (
        <Alert severity="info" sx={{ mb: 2, fontSize: "0.82rem" }}>
          <strong>{t("Specialist Notes")}:</strong> {quote.review_notes}
        </Alert>
      )}

      {/* Edit Mode: Specialist Info Bar */}
      {editMode && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#eff6ff", border: "2px solid #3b82f6" }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", mb: 1 }}>
            {t("Specialist Review")}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label={t("Specialist Name")}
              value={specialistName}
              onChange={(e) => setSpecialistName(e.target.value)}
              size="small"
              required
              error={!specialistName.trim()}
              helperText={!specialistName.trim() ? t("Required") : ""}
              sx={{ minWidth: 200, "& .MuiInputBase-root": { fontSize: "0.82rem" } }}
            />
            <TextField
              label={t("Date")}
              value={new Date().toLocaleDateString("el-GR")}
              size="small"
              disabled
              sx={{ width: 130, "& .MuiInputBase-root": { fontSize: "0.82rem" } }}
            />
            <TextField
              label={t("Notes")}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              size="small"
              multiline
              maxRows={2}
              sx={{ flex: 1, "& .MuiInputBase-root": { fontSize: "0.82rem" } }}
              placeholder={t("Optional notes about changes...")}
            />
          </Stack>
        </Paper>
      )}

      {/* Review File Attachments — edit mode */}
      {editMode && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#fefce8", border: "1px solid #fde68a" }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#92400e", textTransform: "uppercase", mb: 1.5 }}>
            {t("Required Attachments")}
          </Typography>
          <Stack spacing={1.5}>
            {[
              { key: "pdf", label: t("Production PDF"), accept: ".pdf", value: reviewPdf, setter: setReviewPdf, ref: reviewPdfRef },
              { key: "pytha", label: t("Pytha File"), accept: ".pytha,.pyd,.py3d,.dxf,.dwg,.zip", value: reviewPytha, setter: setReviewPytha, ref: reviewPythaRef },
              { key: "csv", label: t("Dimensions CSV"), accept: ".csv,.xlsx,.xls", value: reviewCsv, setter: setReviewCsv, ref: reviewCsvRef },
            ].map(({ key, label, accept, value, setter, ref }) => (
              <Stack key={key} direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ width: 130, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1e293b" }}>{label}</Typography>
                </Box>
                {value ? (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.72rem", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {value.split("/").pop()}
                    </Typography>
                    <IconButton size="small" onClick={() => setter(null)} sx={{ color: "#ef4444", flexShrink: 0 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Stack>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={uploadingFile === key ? <CircularProgress size={14} /> : <FileUploadIcon sx={{ fontSize: 14 }} />}
                    onClick={() => ref.current?.click()}
                    disabled={!!uploadingFile}
                    sx={{ textTransform: "none", fontSize: "0.72rem", fontWeight: 600, borderColor: "#d97706", color: "#92400e", borderRadius: 1.5, "&:hover": { bgcolor: "#fffbeb", borderColor: "#b45309" } }}
                  >
                    {uploadingFile === key ? t("Uploading...") : t("Upload")}
                  </Button>
                )}
                <input
                  ref={ref}
                  type="file"
                  accept={accept}
                  hidden
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleReviewFileUpload(e.target.files[0], key)
                    e.target.value = ""
                  }}
                />
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Review File Attachments — read-only (when not in edit mode) */}
      {!editMode && (quote.review_pdf_url || quote.review_pytha_url || quote.review_csv_url) && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#166534", textTransform: "uppercase", mb: 1 }}>
            {t("Review Attachments")}
          </Typography>
          <Stack spacing={0.75}>
            {[
              { label: t("Production PDF"), url: quote.review_pdf_url },
              { label: t("Pytha File"), url: quote.review_pytha_url },
              { label: t("Dimensions CSV"), url: quote.review_csv_url },
            ].filter(f => f.url).map(({ label, url }) => (
              <Stack key={label} direction="row" alignItems="center" spacing={1}>
                <CheckCircleIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#1e293b" }}>{label}:</Typography>
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: "0.72rem", color: "#2563eb", textDecoration: "underline", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {url.split("/").pop()}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Customer Info */}
      {(quote.customer_name || quote.customer_phone || quote.customer_email) && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#991b1b", textTransform: "uppercase", mb: 0.5 }}>
            {t("Customer")}
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            {quote.customer_name && <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b" }}>{quote.customer_name}</Typography>}
            {quote.customer_phone && <Typography sx={{ fontSize: "0.82rem", color: "#475569" }}>{quote.customer_phone}</Typography>}
            {quote.customer_email && <Typography sx={{ fontSize: "0.82rem", color: "#475569" }}>{quote.customer_email}</Typography>}
          </Stack>
          {quote.customer_address && (
            <Typography sx={{ fontSize: "0.78rem", color: "#64748b", mt: 0.5 }}>{quote.customer_address}</Typography>
          )}
        </Paper>
      )}

      {/* Config Summary */}
      {configChips.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", mb: 0.75 }}>
            {t("Configuration")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {configChips.map((chip, i) => (
              <Chip key={i} label={chip} size="small" sx={{ fontSize: "0.7rem", height: 24, bgcolor: "#f1f5f9", color: "#475569", fontWeight: 500 }} />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Products Table by Section */}
      {sectionOrder.map(sectionKey => {
        const items = groups[sectionKey]
        if (!editMode && (!items || items.length === 0)) return null
        const sectionColor = SECTION_COLORS[sectionKey] || "#64748b"

        return (
          <Box key={sectionKey} sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Box sx={{ width: 4, height: 16, bgcolor: sectionColor, borderRadius: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: sectionColor }}>
                {t(SECTION_LABELS[sectionKey])} ({items?.length || 0})
              </Typography>
              {editMode && (
                <Tooltip title={`${t("Add Product")} → ${t(SECTION_LABELS[sectionKey])}`}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenSearchDrawer(sectionKey)}
                    sx={{
                      bgcolor: sectionColor,
                      color: "#fff",
                      width: 22,
                      height: 22,
                      "&:hover": { bgcolor: sectionColor, opacity: 0.85 },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>{t("Product")}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>W</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>H</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>D</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>{t("Color")}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>{t("Qty")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>B2B</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#475569" }}>{t("Customer")}</TableCell>
                    {editMode && <TableCell sx={{ width: 40 }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(items || []).map((item, idx) => {
                    const b2b = parseFloat(item.b2bsalesprice) || 0
                    const custPrice = calculateFinalPrice(b2b)
                    const qty = parseInt(item.quantity) || 1

                    if (editMode) {
                      return (
                        <TableRow key={item.cartItemKey || idx} sx={{ bgcolor: "#fefce8" }}>
                          <TableCell>
                            <TextField
                              value={item.sku || ""}
                              onChange={(e) => handleEditField(item.cartItemKey, "sku", e.target.value)}
                              size="small"
                              variant="standard"
                              sx={{ width: 80, "& input": { fontSize: "0.72rem", fontFamily: "monospace" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.name || ""}
                              onChange={(e) => handleEditField(item.cartItemKey, "name", e.target.value)}
                              size="small"
                              variant="standard"
                              sx={{ width: 140, "& input": { fontSize: "0.72rem" } }}
                            />
                            {item.doorOption && (
                              <Typography sx={{ fontSize: "0.55rem", color: "#1d4ed8", fontWeight: 600 }}>
                                {item.doorOption}
                              </Typography>
                            )}
                            {item.hingeSurcharge > 0 && (
                              <Typography sx={{ fontSize: "0.55rem", color: "#7c3aed", fontWeight: 600 }}>
                                Κρεμαση {item.hingeCm}cm +€{item.hingeSurcharge}
                              </Typography>
                            )}
                            {item.unit === "Meter" && item.unitQuantity > 0 && (
                              <Typography sx={{ fontSize: "0.55rem", color: "#92400e", fontWeight: 600 }}>
                                {item.unitQuantity.toFixed(2)}m @ €{item.unitRate?.toFixed(2)}/m
                              </Typography>
                            )}
                            {item.unit === "CubicM" && item.unitQuantity > 0 && (
                              <Typography sx={{ fontSize: "0.55rem", color: "#92400e", fontWeight: 600 }}>
                                {item.unitQuantity.toFixed(4)}m³ @ €{item.unitRate?.toFixed(2)}/m³
                                {item.cubicDimensions && ` (${item.cubicDimensions.l}×${item.cubicDimensions.w}×${item.cubicDimensions.h}mm)`}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.customDimensions?.length || item.productWidth || ""}
                              onChange={(e) => handleEditField(item.cartItemKey, "productWidth", e.target.value)}
                              size="small"
                              variant="standard"
                              type="number"
                              sx={{ width: 55, "& input": { fontSize: "0.72rem" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.customDimensions?.height || item.productHeight || ""}
                              onChange={(e) => handleEditField(item.cartItemKey, "productHeight", e.target.value)}
                              size="small"
                              variant="standard"
                              type="number"
                              sx={{ width: 55, "& input": { fontSize: "0.72rem" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.customDimensions?.depth || item.productDepth || ""}
                              onChange={(e) => handleEditField(item.cartItemKey, "productDepth", e.target.value)}
                              size="small"
                              variant="standard"
                              type="number"
                              sx={{ width: 55, "& input": { fontSize: "0.72rem" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>
                                {item.swatch?.label || "-"}
                              </Typography>
                              {item.doorModel?.label && (
                                <Typography sx={{ fontSize: "0.6rem", color: "#0891b2", fontWeight: 600 }}>
                                  {item.doorModel.label}
                                </Typography>
                              )}
                              {item.customRalCode && (
                                <Chip
                                  label={`RAL ${item.customRalCode}`}
                                  size="small"
                                  sx={{ height: 16, fontSize: "0.55rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e", alignSelf: "flex-start" }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              value={qty}
                              onChange={(e) => handleEditField(item.cartItemKey, "quantity", parseInt(e.target.value) || 1)}
                              size="small"
                              variant="standard"
                              type="number"
                              inputProps={{ min: 1 }}
                              sx={{ width: 45, "& input": { fontSize: "0.72rem", textAlign: "center" } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              value={b2b || ""}
                              onChange={(e) => handleEditField(item.cartItemKey, "b2bsalesprice", e.target.value)}
                              size="small"
                              variant="standard"
                              type="number"
                              sx={{ width: 70, "& input": { fontSize: "0.72rem", textAlign: "right" } }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1e293b" }}>
                            {"\u20AC"}{(custPrice * qty).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleRemoveItem(item.cartItemKey)} sx={{ color: "#dc2626" }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )
                    }

                    // Read-only row
                    const pW = parseInt(item.customDimensions?.length || item.productWidth) || 0
                    const pH = parseInt(item.customDimensions?.height || item.productHeight) || 0
                    const pD = parseInt(item.customDimensions?.depth || item.productDepth) || 0
                    return (
                      <TableRow key={item.cartItemKey || idx} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell sx={{ fontSize: "0.68rem", fontFamily: "monospace", color: "#64748b" }}>{item.sku || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#1e293b" }}>
                          {item.name}
                          {item.doorOption && (
                            <Typography component="div" sx={{ fontSize: "0.58rem", color: "#1d4ed8", fontWeight: 600 }}>
                              {item.doorOption}
                            </Typography>
                          )}
                          {item.hingeSurcharge > 0 && (
                            <Typography component="div" sx={{ fontSize: "0.58rem", color: "#7c3aed", fontWeight: 600 }}>
                              Κρεμαση {item.hingeCm}cm +€{item.hingeSurcharge}
                            </Typography>
                          )}
                          {item.unit === "Meter" && item.unitQuantity > 0 && (
                            <Typography component="div" sx={{ fontSize: "0.58rem", color: "#92400e", fontWeight: 600 }}>
                              {item.unitQuantity.toFixed(2)}m @ €{item.unitRate?.toFixed(2)}/m
                            </Typography>
                          )}
                          {item.unit === "CubicM" && item.unitQuantity > 0 && (
                            <Typography component="div" sx={{ fontSize: "0.58rem", color: "#92400e", fontWeight: 600 }}>
                              {item.unitQuantity.toFixed(4)}m³ @ €{item.unitRate?.toFixed(2)}/m³
                              {item.cubicDimensions && ` (${item.cubicDimensions.l}×${item.cubicDimensions.w}×${item.cubicDimensions.h}mm)`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.68rem", color: "#64748b" }}>{pW || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.68rem", color: "#64748b" }}>{pH || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.68rem", color: "#64748b" }}>{pD || "-"}</TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {item.swatch?.swatchUrl && (
                                <Box
                                  component="img"
                                  src={item.swatch.swatchUrl}
                                  sx={{ width: 14, height: 14, borderRadius: "2px", objectFit: "cover" }}
                                  onError={(e) => { e.target.style.display = "none" }}
                                />
                              )}
                              <Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>{item.swatch?.label || "-"}</Typography>
                            </Stack>
                            {item.doorModel?.label && (
                              <Typography sx={{ fontSize: "0.6rem", color: "#0891b2", fontWeight: 600 }}>
                                {item.doorModel.label}
                              </Typography>
                            )}
                            {item.customRalCode && (
                              <Chip
                                label={`RAL ${item.customRalCode}`}
                                size="small"
                                sx={{ height: 16, fontSize: "0.55rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e", alignSelf: "flex-start" }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.72rem", fontWeight: 700 }}>{qty}</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.72rem", color: "#64748b" }}>{"\u20AC"}{(b2b * qty).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1e293b" }}>{"\u20AC"}{(custPrice * qty).toFixed(2)}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      })}

      {/* Pytha 3D Files */}
      <PythaFilesSection cart={cart} t={t} filesByProduct={filesByProduct} loadingFiles={loadingFiles} />

      <Divider sx={{ my: 2 }} />

      {/* Totals */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", mb: 3 }}>
        <Stack spacing={1}>
          {ralCodes.length > 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: "0.78rem", color: "#d97706" }}>
                {t("RAL surcharge")} ({ralCodes.length} {"×"} {"€"}{RAL_SURCHARGE_PER_CODE}) {"—"} {ralCodes.join(", ")}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#d97706" }}>
                +{"€"}{ralSurchargeTotal.toFixed(2)}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>B2B {t("Total")}</Typography>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{"\u20AC"}{b2bTotal.toFixed(2)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>{t("Customer")} {t("Total")}</Typography>
            <Typography sx={{ fontSize: "0.92rem", fontWeight: 800, color: "#1e293b" }}>{"\u20AC"}{customerTotal.toFixed(2)}</Typography>
          </Stack>
          {profitMargin > 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: "0.78rem", color: "#16a34a" }}>{t("Margin")} ({profitMargin}%)</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#16a34a" }}>+{"\u20AC"}{marginAmount.toFixed(2)}</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Action Buttons */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {/* Edit mode actions */}
        {editMode ? (
          <>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSaveAndSendBack}
              disabled={saving || !specialistName.trim()}
              sx={{
                textTransform: "none", fontWeight: 700, bgcolor: "#1d4ed8", borderRadius: 2,
                "&:hover": { bgcolor: "#1e40af" },
              }}
            >
              {t("Save & Send Back")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleCancelEdit}
              sx={{ textTransform: "none", fontWeight: 600, borderColor: "#94a3b8", color: "#64748b", borderRadius: 2 }}
            >
              {t("Cancel")}
            </Button>

            {/* CSV Import — only in edit mode */}
            <Button
              variant="outlined"
              startIcon={importing ? <CircularProgress size={16} /> : <FileUploadIcon sx={{ fontSize: 16 }} />}
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              sx={{ textTransform: "none", fontWeight: 600, borderColor: "#d97706", color: "#d97706", borderRadius: 2, "&:hover": { bgcolor: "#fffbeb", borderColor: "#b45309" } }}
            >
              {importing ? t("Processing...") : t("Import CSV")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleCsvImport}
            />
          </>
        ) : (
          <>
            {/* Approve / Request Changes / Edit Order — only for pending_approval */}
            {quote.status === "pending_approval" && (
              <>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => onApprove(quote.id)}
                  sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#16a34a", borderRadius: 2, "&:hover": { bgcolor: "#15803d" } }}
                >
                  {t("Approve")}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEnterEditMode}
                  sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#1d4ed8", borderRadius: 2, "&:hover": { bgcolor: "#1e40af" } }}
                >
                  {t("Edit Order")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditNoteIcon />}
                  onClick={() => onRequestChanges(quote.id)}
                  sx={{ textTransform: "none", fontWeight: 700, borderColor: "#dc2626", color: "#dc2626", borderRadius: 2, "&:hover": { bgcolor: "#fef2f2", borderColor: "#b91c1c" } }}
                >
                  {t("Request Changes")}
                </Button>
              </>
            )}

            {/* Pytha Files ZIP Export — always visible */}
            <Tooltip title={!hasPythaFiles ? t("No Pytha files found for products in this quote") : ""} arrow>
              <span>
                <Button
                  variant="outlined"
                  startIcon={zipping ? <CircularProgress size={16} /> : <FolderZipIcon sx={{ fontSize: 16 }} />}
                  onClick={handleZipExport}
                  disabled={zipping || !hasPythaFiles}
                  sx={{ textTransform: "none", fontWeight: 600, borderColor: "#8b5cf6", color: "#8b5cf6", borderRadius: 1.5, "&:hover": { bgcolor: "#faf5ff", borderColor: "#7c3aed" } }}
                >
                  {zipping ? t("Zipping...") : t("Pytha Files Export")}
                  {hasPythaFiles && (
                    <Chip
                      label={Object.values(filesByProduct).reduce((s, f) => s + f.length, 0)}
                      size="small"
                      sx={{ ml: 0.75, height: 16, fontSize: "0.55rem", fontWeight: 700, bgcolor: "#ede9fe", color: "#7c3aed" }}
                    />
                  )}
                </Button>
              </span>
            </Tooltip>

            {/* Create ERP Order — only for final_accepted */}
            {quote.status === "final_accepted" && (
              <Button
                variant="contained"
                startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
                onClick={() => setCreateOrderDialogOpen(true)}
                sx={{
                  textTransform: "none", fontWeight: 700, bgcolor: "#16a34a", borderRadius: 2,
                  "&:hover": { bgcolor: "#15803d" },
                }}
              >
                {t("Create Order")}
              </Button>
            )}

            {/* PDF exports — always available, but Customer PDF only for final_accepted */}
            {quote.status === "final_accepted" && (
              <Button
                variant="outlined"
                startIcon={<PersonIcon sx={{ fontSize: 16 }} />}
                onClick={handleExportCustomer}
                sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#475569", borderRadius: 1.5, "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" } }}
              >
                {t("Customer PDF")}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<LocalShippingIcon sx={{ fontSize: 16 }} />}
              onClick={handleExportSupplier}
              sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#475569", borderRadius: 1.5, "&:hover": { borderColor: "#16a34a", color: "#16a34a" } }}
            >
              B2B PDF
            </Button>

            {/* CSV Export */}
            <Button
              variant="outlined"
              startIcon={<TableChartIcon sx={{ fontSize: 16 }} />}
              onClick={handleCsvExport}
              sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#475569", borderRadius: 1.5, "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" } }}
            >
              {t("Export CSV")}
            </Button>

          </>
        )}
      </Stack>

      {/* Product Search Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 380, p: 0 } }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#1e293b" }}>
              {t("Add Product")}
            </Typography>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
          <Chip
            label={t(SECTION_LABELS[drawerSection])}
            size="small"
            sx={{
              mb: 1.5,
              fontWeight: 700,
              fontSize: "0.7rem",
              bgcolor: SECTION_COLORS[drawerSection] || "#64748b",
              color: "#fff",
            }}
          />
          <TextField
            fullWidth
            size="small"
            placeholder={t("Search by SKU or product name...")}
            value={searchText}
            onChange={(e) => handleSearchProducts(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
              endAdornment: searchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
            }}
            sx={{ "& .MuiInputBase-root": { fontSize: "0.85rem" } }}
          />
          {searchText.trim().length > 0 && searchText.trim().length < 2 && (
            <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", mt: 0.5 }}>
              {t("Type at least 2 characters...")}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
          {/* Expanded product — stepped Model → Color → Size selection */}
          {expandedProduct && (() => {
            const variants = expandedProduct._variants || []
            // Unique model codes from variants
            const uniqueModels = [...new Set(variants.map(v => v.finish_code).filter(Boolean))].sort()
            const hasModels = uniqueModels.length > 0
            // If product has no finish codes (NOCODE), skip model/color steps
            const isNocode = !hasModels || (uniqueModels.length === 1 && uniqueModels[0] === "NOCODE")

            // Swatches for selected model — Step 2 picks color only (door-model is a separate concept)
            const modelSwatches = drawerModel
              ? splitSwatches(swatchesByGroup[drawerModel] || []).colors
              : []

            // Size variants filtered by selected model
            const modelVariants = drawerModel
              ? variants.filter(v => v.finish_code === drawerModel)
              : variants
            // Unique sizes (W × H)
            const uniqueSizes = []
            const seenSizes = new Set()
            for (const v of modelVariants) {
              const key = `${v.width || 0}x${v.height || 0}`
              if (!seenSizes.has(key)) {
                seenSizes.add(key)
                uniqueSizes.push(v)
              }
            }

            return (
            <Paper elevation={0} sx={{ mb: 1.5, p: 1.5, borderRadius: 1.5, border: "2px solid #3b82f6", bgcolor: "#eff6ff" }}>
              {/* Product header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {expandedProduct.prdimage && (
                    <Box
                      component="img"
                      src={expandedProduct.prdimage}
                      sx={{ width: 36, height: 36, borderRadius: 1, objectFit: "contain", border: "1px solid #bfdbfe", bgcolor: "#fff" }}
                      onError={(e) => { e.target.style.display = "none" }}
                    />
                  )}
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#1e293b" }}>
                      {expandedProduct.name}
                    </Typography>
                    <Typography sx={{ fontFamily: "monospace", fontSize: "0.7rem", color: "#3b82f6" }}>
                      {expandedProduct.sku}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small" onClick={() => { setExpandedProduct(null); setDrawerModel(""); setDrawerSwatch(null) }} sx={{ color: "#64748b" }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>

              {/* Step 1: Model selection (skip for NOCODE products) */}
              {!isNocode && (
                <Box sx={{ mb: 1.25 }}>
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", mb: 0.5 }}>
                    1. {t("Model")} ({uniqueModels.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {uniqueModels.map(code => {
                      const isActive = drawerModel === code
                      return (
                        <Chip
                          key={code}
                          label={code}
                          size="small"
                          variant={isActive ? "filled" : "outlined"}
                          onClick={() => {
                            setDrawerModel(isActive ? "" : code)
                            setDrawerSwatch(null)
                          }}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            height: 28,
                            minWidth: 40,
                            cursor: "pointer",
                            ...(isActive
                              ? { bgcolor: "#e50019", color: "#fff", borderColor: "transparent" }
                              : { borderColor: "#d1d5db", color: "#475569" }),
                            "&:hover": isActive ? {} : { borderColor: "#e50019", color: "#e50019" },
                          }}
                        />
                      )
                    })}
                  </Box>
                </Box>
              )}

              {/* Step 2: Color selection (only after model is selected) */}
              {!isNocode && drawerModel && modelSwatches.length > 0 && (
                <Box sx={{ mb: 1.25 }}>
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", mb: 0.5 }}>
                    2. {t("Color")} ({modelSwatches.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 140, overflow: "auto" }}>
                    {modelSwatches.map((sw, idx) => {
                      const isActive = drawerSwatch?.label === sw.label
                      return (
                        <Tooltip key={idx} title={sw.label} arrow placement="top">
                          <Box
                            onClick={() => setDrawerSwatch(isActive ? null : sw)}
                            sx={{
                              width: 36, height: 36,
                              borderRadius: 1,
                              border: isActive ? "2px solid #3b82f6" : "1px solid #e2e8f0",
                              overflow: "hidden",
                              cursor: "pointer",
                              flexShrink: 0,
                              backgroundImage: sw.swatchUrl ? `url(${sw.swatchUrl})` : "none",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              bgcolor: sw.hexColor || "#e2e8f0",
                              boxShadow: isActive ? "0 0 0 2px #3b82f640" : "none",
                              transition: "all 0.12s",
                              "&:hover": { borderColor: "#3b82f6" },
                            }}
                          />
                        </Tooltip>
                      )
                    })}
                  </Box>
                  {drawerSwatch && (
                    <Typography sx={{ fontSize: "0.65rem", color: "#3b82f6", fontWeight: 600, mt: 0.5 }}>
                      {drawerSwatch.label}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Step 2b: No swatches available for model */}
              {!isNocode && drawerModel && modelSwatches.length === 0 && (
                <Box sx={{ mb: 1.25 }}>
                  <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", fontStyle: "italic" }}>
                    {t("No color options for this model")}
                  </Typography>
                </Box>
              )}

              {/* Step 3: Size selection — shown after model+color selected (or directly for NOCODE) */}
              {(isNocode || drawerModel) && (
                <Box>
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", mb: 0.5 }}>
                    {isNocode ? t("Size Variants") : `3. ${t("Size")}`} ({uniqueSizes.length})
                  </Typography>
                  <Stack spacing={0.5} sx={{ maxHeight: 240, overflow: "auto" }}>
                    {uniqueSizes.map((v, idx) => (
                      <Stack
                        key={v.sku || idx}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        onClick={() => {
                          handleAddFromSearch(expandedProduct, v, drawerSwatch, drawerModel)
                        }}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: "#fff",
                          border: "1px solid #e2e8f0",
                          cursor: "pointer",
                          transition: "all 0.12s",
                          "&:hover": { borderColor: "#3b82f6", bgcolor: "#dbeafe" },
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontFamily: "monospace", fontSize: "0.72rem", fontWeight: 700, color: "#1e293b" }}>
                            {v.sku || expandedProduct.sku}
                          </Typography>
                          <Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>
                            {v.width || 0} x {v.height || 0} mm
                          </Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#1e293b" }}>
                            {"\u20AC"}{(parseFloat(v.b2bsalesprice) || 0).toFixed(2)}
                          </Typography>
                          <AddIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Selection summary */}
              {!isNocode && drawerModel && (
                <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #bfdbfe" }}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap">
                    <Chip label={`${t("Model")}: ${drawerModel}`} size="small" sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#fee2e2", color: "#991b1b" }} />
                    {drawerSwatch && (
                      <Chip
                        label={`${t("Color")}: ${drawerSwatch.label}`}
                        size="small"
                        icon={drawerSwatch.swatchUrl ? (
                          <Box
                            component="img"
                            src={drawerSwatch.swatchUrl}
                            sx={{ width: 12, height: 12, borderRadius: "2px", objectFit: "cover" }}
                            onError={(e) => { e.target.style.display = "none" }}
                          />
                        ) : undefined}
                        sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#dbeafe", color: "#1d4ed8", "& .MuiChip-icon": { ml: 0.5 } }}
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Paper>
            )
          })()}

          {searchResults.length === 0 && !searchLoading && searchText.trim().length >= 2 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8" }}>
                {t("No products found")}
              </Typography>
            </Box>
          )}
          {searchResults.length === 0 && !searchLoading && searchText.trim().length < 2 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <SearchIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
              <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8" }}>
                {t("Search products by SKU or name")}
              </Typography>
            </Box>
          )}
          <Stack spacing={0.75}>
            {searchResults.map(product => {
              const alreadyInCart = editedCart.some(c => c.sku === product.sku)
              return (
                <Paper
                  key={product.id}
                  elevation={0}
                  onClick={() => handleProductClick(product)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: "1px solid #e2e8f0",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: "#3b82f6", bgcolor: "#eff6ff", boxShadow: "0 2px 6px rgba(0,0,0,0.06)" },
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    {/* Product SVG thumbnail */}
                    {product.prdimage ? (
                      <Box
                        component="img"
                        src={product.prdimage}
                        sx={{
                          width: 48, height: 48, borderRadius: 1,
                          objectFit: "contain", border: "1px solid #e2e8f0",
                          bgcolor: "#f8fafc", flexShrink: 0,
                        }}
                        onError={(e) => { e.target.style.display = "none" }}
                      />
                    ) : (
                      <Box sx={{
                        width: 48, height: 48, borderRadius: 1, bgcolor: "#f1f5f9",
                        border: "1px solid #e2e8f0", display: "flex",
                        alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>N/A</Typography>
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.3 }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 700, color: "#3b82f6" }}>
                          {product.sku}
                        </Typography>
                        {alreadyInCart && (
                          <Chip label={t("in cart")} size="small" sx={{ height: 16, fontSize: "0.5rem", fontWeight: 700, bgcolor: "#dcfce7", color: "#166534" }} />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {product.name}
                      </Typography>
                      {(product.width || product.height || product.depth) && (
                        <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", mt: 0.25 }}>
                          {product.width || 0} x {product.height || 0} x {product.depth || 0} mm
                        </Typography>
                      )}
                    </Box>
                    <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#1e293b" }}>
                        {"\u20AC"}{(parseFloat(product.b2bsalesprice) || 0).toFixed(2)}
                      </Typography>
                      <AddIcon sx={{ fontSize: 16, color: "#3b82f6", mt: 0.5 }} />
                    </Stack>
                  </Stack>
                </Paper>
              )
            })}
          </Stack>
        </Box>
      </Drawer>

      {/* Import Preview Dialog */}
      <ImportPreviewDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onApply={handleApplyImport}
        matchedProducts={importMatchedProducts}
        missingSkus={importMissingSkus}
        t={t}
      />

      {/* Create Order Confirmation Dialog */}
      <Dialog open={createOrderDialogOpen} onClose={() => !creatingOrder && setCreateOrderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("Create ERP Order")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: "#64748b", mb: 2 }}>
            {t("Create an ERP order from this quote? This will add a new order to the system.")}
          </Typography>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <Stack spacing={1}>
              {quote.customer_name && (
                <Stack direction="row" spacing={1}>
                  <Typography sx={{ fontSize: "0.82rem", color: "#64748b", minWidth: 80 }}>{t("Customer")}:</Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{quote.customer_name}</Typography>
                </Stack>
              )}
              <Stack direction="row" spacing={1}>
                <Typography sx={{ fontSize: "0.82rem", color: "#64748b", minWidth: 80 }}>{t("Quote")}:</Typography>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{quote.title || "Untitled"}</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography sx={{ fontSize: "0.82rem", color: "#64748b", minWidth: 80 }}>{t("Items")}:</Typography>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                  {displayCart.reduce((s, i) => s + (parseInt(i.quantity) || 1), 0)} {t("products")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography sx={{ fontSize: "0.82rem", color: "#64748b", minWidth: 80 }}>{t("Total")}:</Typography>
                <Typography sx={{ fontSize: "0.92rem", fontWeight: 800, color: "#1e293b" }}>
                  {"\u20AC"}{customerTotal.toFixed(2)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateOrderDialogOpen(false)}
            disabled={creatingOrder}
            sx={{ textTransform: "none" }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateOrder}
            disabled={creatingOrder}
            startIcon={creatingOrder ? <CircularProgress size={16} color="inherit" /> : <ShoppingCartIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
          >
            {creatingOrder ? t("Creating...") : t("Create Order")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Created Success Dialog */}
      <Dialog open={!!orderCreated} onClose={() => setOrderCreated(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#16a34a" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon sx={{ color: "#16a34a" }} />
            <span>{t("Order Created")}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.92rem", color: "#1e293b" }}>
            {t("Order")} #{orderCreated?.id} {t("has been created successfully.")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOrderCreated(null)}
            sx={{ textTransform: "none" }}
          >
            {t("Back to List")}
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/order-detail/${orderCreated?.id}`)}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
          >
            {t("View Order")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Missing Files Warning Dialog */}
      <Dialog open={missingFilesDialogOpen} onClose={() => setMissingFilesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#dc2626" }}>
          {t("Missing Attachments")}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("The following required files have not been uploaded:")}
          </Alert>
          <Stack spacing={1}>
            {!reviewPdf && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CloseIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b" }}>{t("Production PDF")}</Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>— {t("Extraction for production")}</Typography>
              </Stack>
            )}
            {!reviewPytha && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CloseIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b" }}>{t("Pytha File")}</Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>— {t("3D design file of the order")}</Typography>
              </Stack>
            )}
            {!reviewCsv && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CloseIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b" }}>{t("Dimensions CSV")}</Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>— {t("File with dimensions data")}</Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMissingFilesDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            {t("Go Back")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setMissingFilesDialogOpen(false)
              doSaveAndSendBack()
            }}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" } }}
          >
            {t("Proceed Anyway")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ─── Main Page ──────────────────────────────────────────────────
const QuoteReviewPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedQuote, setSelectedQuote] = useState(null)
  const [statusFilter, setStatusFilter] = useState("pending_approval")
  const [changesNote, setChangesNote] = useState("")
  const [changesDialogOpen, setChangesDialogOpen] = useState(false)
  const [changesQuoteId, setChangesQuoteId] = useState(null)

  const fetchQuotes = useCallback(async () => {
    setLoading(true)
    try {
      const data = statusFilter === "all"
        ? await getAllQuotes()
        : await getQuotesByStatus(statusFilter)
      setQuotes(data)
      // Auto-select first if current selection not in results
      if (data.length > 0 && (!selectedQuote || !data.find(q => q.id === selectedQuote.id))) {
        setSelectedQuote(data[0])
      }
      if (data.length === 0) setSelectedQuote(null)
    } catch (err) {
      console.error("[QuoteReview] Fetch error:", err)
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    fetchQuotes()
  }, [fetchQuotes])

  const handleApprove = async (id) => {
    try {
      await updateQuote({ id, status: targetStatus, updated_at: new Date().toISOString() })
      await fetchQuotes()
    } catch (err) {
      console.error("[QuoteReview] Approve error:", err)
    }
  }

  const handleRequestChanges = (id) => {
    setChangesQuoteId(id)
    setChangesNote("")
    setChangesDialogOpen(true)
  }

  const handleSubmitChanges = async () => {
    if (!changesQuoteId) return
    try {
      await updateQuote({
        id: changesQuoteId,
        status: "changes_requested",
        review_notes: changesNote || "",
        updated_at: new Date().toISOString(),
      })
      setChangesDialogOpen(false)
      await fetchQuotes()
    } catch (err) {
      console.error("[QuoteReview] Request changes error:", err)
    }
  }

  const handleSaveEdits = async (quoteId, updates) => {
    try {
      await updateQuote({ id: quoteId, ...updates })
      await fetchQuotes()
    } catch (err) {
      console.error("[QuoteReview] Save edits error:", err)
      throw err
    }
  }

  return (
    <Box sx={{ display: "flex", height: "100vh", bgcolor: "#f8fafc" }}>
      {/* LEFT: Quote List */}
      <Box
        sx={{
          width: 360,
          minWidth: 320,
          borderRight: "1px solid #e2e8f0",
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 2, borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", mb: 1.5 }}>
            {t("Quote Reviews")}
          </Typography>
          {/* Status filter chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {STATUS_FILTERS.map(sf => {
              const isActive = statusFilter === sf.value
              return (
                <Chip
                  key={sf.value}
                  label={t(sf.label)}
                  size="small"
                  variant={isActive ? "filled" : "outlined"}
                  onClick={() => setStatusFilter(sf.value)}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    height: 26,
                    cursor: "pointer",
                    ...(isActive
                      ? { bgcolor: "#1e293b", color: "#fff" }
                      : { borderColor: "#d1d5db", color: "#6b7280" }),
                    "&:hover": isActive ? {} : { borderColor: "#1e293b", color: "#1e293b" },
                  }}
                />
              )
            })}
          </Box>
        </Box>

        {/* List */}
        <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : quotes.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <DescriptionIcon sx={{ fontSize: 40, color: "#d1d5db", mb: 1 }} />
              <Typography sx={{ color: "#94a3b8", fontSize: "0.82rem" }}>
                {t("No quotes found")}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {quotes.map(q => (
                <QuoteCard
                  key={q.id}
                  quote={q}
                  isSelected={selectedQuote?.id === q.id}
                  onClick={() => setSelectedQuote(q)}
                  t={t}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {/* RIGHT: Quote Detail */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {selectedQuote ? (
          <QuoteDetail
            quote={selectedQuote}
            onApprove={handleApprove}
            onRequestChanges={handleRequestChanges}
            onSaveEdits={handleSaveEdits}
            onRefresh={fetchQuotes}
            navigate={navigate}
            t={t}
          />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <Stack alignItems="center" spacing={1}>
              <DescriptionIcon sx={{ fontSize: 48, color: "#d1d5db" }} />
              <Typography sx={{ color: "#94a3b8" }}>{t("Select a quote to review")}</Typography>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Changes Request Dialog */}
      <Dialog open={changesDialogOpen} onClose={() => setChangesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("Request Changes")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: "#64748b", mb: 2 }}>
            {t("Optionally add a note explaining what needs to be changed.")}
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder={t("e.g. Please adjust the base cabinet dimensions...")}
            value={changesNote}
            onChange={(e) => setChangesNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangesDialogOpen(false)} sx={{ textTransform: "none" }}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitChanges}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }}
          >
            {t("Submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default QuoteReviewPage
